// Debounced user search for the share-thread viewer picker.
// Same debounce/stale-response-guard shape as OwnersTab's useUserSearch
// (components/settings/agent/OwnersTab.tsx) — kept local to this feature
// since it is the only other caller of this exact pattern today.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { UserOptionView } from '@/entities/owner';
import { log } from '@/shared/logger';
import { searchShareUsers } from '../api/searchShareUsers';

const SEARCH_DEBOUNCE_MS = 200;

export function useSearchShareUsers(): {
  results: UserOptionView[];
  loading: boolean;
  search: (query: string) => void;
} {
  const [results, setResults] = useState<UserOptionView[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  const search = useCallback((query: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      try {
        const users = await searchShareUsers(query);
        if (reqId === reqIdRef.current) setResults(users);
      } catch (err) {
        log.warn('share_thread_user_search_failed', { query, error: err });
        if (reqId === reqIdRef.current) setResults([]);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { results, loading, search };
}
